import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { X } from 'lucide-react'
import { useTeams } from '@/features/admin/teams/hooks'
import { useCreateThread } from '../hooks'
import type { Thread, ThreadVisibility } from '../types'

type Props = {
  entityRef: string
  open: boolean
  onClose: () => void
  onCreated?: (thread: Thread) => void
}

/**
 * Tworzenie wątku collab z widocznością.
 * - SCOPED: wybór zespołów -> audienceTokens `easy:team:<teamId>` (namespace zgodny z
 *   backendowym AudienceTokenResolver, który buduje `easy:team:<teamId>` z id zespołów).
 * - PRIVATE: brak tokenów (widzą tylko twórca + później @wzmiankowani).
 */
export function NewThreadDialog({ entityRef, open, onClose, onCreated }: Props) {
  const create = useCreateThread()
  const { data: teams = [], isLoading: teamsLoading } = useTeams(true)

  const [title, setTitle] = useState('')
  const [visibility, setVisibility] = useState<ThreadVisibility>('SCOPED')
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([])

  const toggleTeam = (id: number) => {
    setSelectedTeamIds(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id],
    )
  }

  const titleEmpty = title.trim().length === 0
  const noTeamsSelected = visibility === 'SCOPED' && selectedTeamIds.length === 0

  const submit = () => {
    if (titleEmpty) return
    const audienceTokens =
      visibility === 'SCOPED' ? selectedTeamIds.map(id => `easy:team:${id}`) : []
    create.mutate(
      { entityRef, title: title.trim(), visibility, audienceTokens },
      {
        onSuccess: (thread: Thread) => {
          onCreated?.(thread)
          onClose()
        },
      },
    )
  }

  const labelCls = 'text-xs font-medium uppercase tracking-[0.06em] text-slate-600'
  const inputCls = 'w-full rounded border border-slate-300 px-2 py-1 text-sm'

  return (
    <Dialog.Root open={open} onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-950/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded bg-white p-5 shadow-xl">
          <div className="mb-4 flex items-start justify-between gap-3">
            <Dialog.Title className="text-base font-semibold">Nowy wątek</Dialog.Title>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Zamknij">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-3">
            <label className="grid gap-1">
              <span className={labelCls}>Tytuł</span>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                disabled={create.isPending}
                className={inputCls}
                placeholder="Krótki temat wątku"
                autoFocus
              />
            </label>

            <div className="grid gap-1">
              <span className={labelCls}>Widoczność</span>
              <div className="flex rounded border bg-white overflow-hidden text-sm">
                {(['SCOPED', 'PRIVATE'] as ThreadVisibility[]).map(v => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={visibility === v}
                    aria-label={v === 'SCOPED' ? 'Zespół' : 'Prywatny'}
                    onClick={() => setVisibility(v)}
                    disabled={create.isPending}
                    className={[
                      'flex-1 px-2 py-1.5',
                      visibility === v ? 'bg-sky-600 text-white' : 'text-slate-600 hover:bg-slate-100',
                    ].join(' ')}
                  >
                    {v === 'SCOPED' ? 'Zespół' : 'Prywatny'}
                  </button>
                ))}
              </div>
            </div>

            {visibility === 'SCOPED' && (
              <div className="grid gap-1">
                <span className={labelCls}>Widoczne dla</span>
                {teamsLoading && <p className="text-xs text-slate-500">Ładowanie zespołów…</p>}
                {!teamsLoading && teams.length === 0 && (
                  <p className="text-xs text-slate-500">Brak zespołów</p>
                )}
                {teams.length > 0 && (
                  <div className="max-h-40 overflow-auto rounded border border-slate-200 p-2 grid gap-1">
                    {teams.map(t => (
                      <label key={t.id} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          aria-label={t.name}
                          checked={selectedTeamIds.includes(t.id)}
                          onChange={() => toggleTeam(t.id)}
                          disabled={create.isPending}
                          className="size-4"
                        />
                        <span>{t.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {noTeamsSelected && (
                  <p className="text-xs text-amber-600">
                    Bez wybranych zespołów wątek zobaczysz tylko Ty
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={create.isPending}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={titleEmpty || create.isPending}
              className="rounded bg-sky-600 px-3 py-1.5 text-sm text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {create.isPending ? 'Tworzenie…' : 'Utwórz'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
